<template>
    <div class="economic-config mt-3">
        <div class="card">
            <div class="card-header">
                <h5>{{ $t("economic_configuration") }}</h5>
            </div>
            <div class="card-body">
                <!-- Grundkonfiguration (nur für Superuser sichtbar) -->
                <div class="row mb-3" v-if="isSuperuser">
                    <div class="col-md-6">
                        <div class="form-floating">
                            <input id="annualTarget" type="number" step="0.01"
                                class="form-control" v-model="config.annual_target_kwh"
                                placeholder="0">
                            <label for="annualTarget">{{ $t("annual_target_kwh") }}</label>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <label>{{ $t("production_kwh_field") }}</label>
                        <model-select id="productionField"
                            :options="allDescriptions"
                            v-model="config.production_kwh_field"
                        ></model-select>
                    </div>
                </div>

                <!-- Strompreise mit Gültigkeitszeitraum -->
                <div class="row mb-3">
                    <div class="col-12">
                        <ElectricityPriceList
                            :prices="config.electricity_prices"
                            @update:prices="onPricesUpdated"
                        ></ElectricityPriceList>
                    </div>
                </div>

                <!-- Monatliche Verteilung -->
                <MonthlyDistribution
                    :distributions="config.monthly_distributions"
                    :annualTarget="parseFloat(config.annual_target_kwh) || 0"
                    @updateDistributions="onDistributionsUpdated"
                ></MonthlyDistribution>

                <!-- Speichern Button -->
                <div class="row mt-4">
                    <div class="col-12">
                        <button class="btn btn-primary" @click="saveConfig" :disabled="saving">
                            <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                            {{ $t("save") }}
                        </button>
                        <span v-if="saveSuccess" class="text-success ms-3">
                            <i class="fa-solid fa-check"></i> {{ $t("saved") }}
                        </span>
                        <span v-if="saveError" class="text-danger ms-3">
                            {{ saveError }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ModelSelect } from 'vue-search-select'
import MonthlyDistribution from '@/components/wirtschaftlichkeit/MonthlyDistribution.vue'
import ElectricityPriceList from '@/components/wirtschaftlichkeit/ElectricityPriceList.vue'
import apiClient from '@/services/apiClient'

export default {
    name: 'EconomicConfig',
    components: {
        ModelSelect,
        MonthlyDistribution,
        ElectricityPriceList
    },
    props: {
        plant: {
            type: Object,
            required: true
        },
        economicConfig: {
            type: Object,
            default: null
        },
        allDescriptions: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            config: {
                electricity_prices: [],
                production_kwh_field: null,
                annual_target_kwh: 0,
                monthly_distributions: this.getDefaultDistributions()
            },
            saving: false,
            saveSuccess: false,
            saveError: null
        }
    },
    computed: {
        isSuperuser() {
            return this.$store.state.me?.is_superuser || false
        }
    },
    watch: {
        economicConfig: {
            handler(newVal) {
                if (newVal) {
                    this.config = {
                        id: newVal.id,
                        electricity_prices: newVal.electricity_prices || [],
                        production_kwh_field: newVal.production_kwh_field,
                        annual_target_kwh: newVal.annual_target_kwh || 0,
                        monthly_distributions: newVal.monthly_distributions && newVal.monthly_distributions.length > 0
                            ? newVal.monthly_distributions
                            : this.getDefaultDistributions()
                    }
                }
            },
            immediate: true
        }
    },
    methods: {
        getDefaultDistributions() {
            const distributions = []
            for (let month = 1; month <= 12; month++) {
                distributions.push({
                    month: month,
                    percentage: 8.33
                })
            }
            return distributions
        },
        onDistributionsUpdated(distributions) {
            this.config.monthly_distributions = distributions
        },
        onPricesUpdated(prices) {
            this.config.electricity_prices = prices
        },
        async saveConfig() {
            this.saving = true
            this.saveSuccess = false
            this.saveError = null

            try {
                const payload = {
                    plant: this.plant.id,
                    electricity_prices: this.config.electricity_prices.map(p => ({
                        price_per_kwh: p.price_per_kwh,
                        valid_from: p.valid_from,
                        valid_to: p.valid_to || null
                    })),
                    monthly_distributions: this.config.monthly_distributions.map(d => ({
                        month: d.month,
                        percentage: d.percentage
                    }))
                }

                // Nur Superuser dürfen diese Felder ändern
                if (this.isSuperuser) {
                    payload.production_kwh_field = this.config.production_kwh_field
                    payload.annual_target_kwh = this.config.annual_target_kwh
                }

                let response
                if (this.config.id) {
                    // Update existing
                    response = await apiClient.updateEconomicConfig(this.config.id, payload)
                } else {
                    // Create new
                    response = await apiClient.createEconomicConfig(payload)
                }

                this.config.id = response.data.id
                this.saveSuccess = true
                this.$emit('configSaved', response.data)

                // Reset success message after 3 seconds
                setTimeout(() => {
                    this.saveSuccess = false
                }, 3000)

            } catch (error) {
                console.error('Error saving economic config:', error)
                this.saveError = error.response?.data?.detail || this.$t('save_error')
            } finally {
                this.saving = false
            }
        }
    }
}
</script>
